import React, { useCallback, useEffect, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { S, labelStyle } from './shellTokens';

type Range = '7' | '30' | '90';
type Metrics = {
  views?: { total_views?: number; unique_visitors?: number; unique_sessions?: number };
  duration?: { average_duration_formatted?: string; average_duration_seconds?: number };
  scroll?: { milestone_percentages?: Record<string, number>; average_max_depth?: number };
  clicks?: { total_clicks?: number; top_elements?: Array<{ element: string; clicks: number; text?: string; tagName?: string; href?: string }> };
};

type Props = { pageId: string; path: string; publishedVersion?: string | null };

// Same rules as the page analytics view: the deployed path, and its date ranges.
export const analyticsPath = (path: string) => (path.startsWith('/cxc10') ? path.replace(/^\/cxc10/, '') : path);
export const dateRange = (days: number, now: Date = new Date()) => ({
  start_date: new Date(now.getFullYear(), now.getMonth(), now.getDate() - days, 0, 0, 0, 0).toISOString(),
  end_date: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999).toISOString()
});

const number = (n?: number) => (typeof n === 'number' ? n.toLocaleString('en-US') : '–');
const MILESTONES = ['25', '50', '75', '100'];

const useStyles = createUseStyles({
  root: { display: 'grid', gap: 18, fontFamily: S.ui, color: S.fg, fontSize: 13 },
  row: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  version: { fontFamily: S.mono, fontSize: 12, color: S.muted },
  segment: { display: 'flex', padding: 2, gap: 2, borderRadius: 7, background: S.line },
  seg: { all: 'unset', cursor: 'pointer', padding: '2px 8px', borderRadius: 5, fontSize: 12, fontWeight: 600, color: S.muted, '&:hover': { color: S.fg } },
  segOn: { background: S.field, color: S.fg },
  stats: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 },
  stat: { padding: '10px 12px', borderRadius: 8, background: S.field, border: `1px solid ${S.line}` },
  statValue: { fontSize: 20, fontWeight: 600, fontVariantNumeric: 'tabular-nums' },
  statLabel: { ...labelStyle, fontSize: 11, marginTop: 2 },
  label: { ...labelStyle, marginBottom: 8 },
  bar: { display: 'grid', gridTemplateColumns: '44px minmax(0, 1fr) 44px', alignItems: 'center', gap: 8, margin: '5px 0', fontFamily: S.mono, fontSize: 12 },
  track: { height: 8, borderRadius: 4, background: S.line, overflow: 'hidden' },
  fill: { height: '100%', background: S.fg },
  clicks: { listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 4 },
  click: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 8, fontSize: 13 },
  clickText: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  muted: { margin: 0, color: S.muted, lineHeight: 1.5 },
  error: { margin: 0, color: S.red, lineHeight: 1.5 },
  link: { color: S.fg, fontWeight: 600 },
  ask: { display: 'grid', gap: 6 },
  input: { boxSizing: 'border-box', width: '100%', padding: '8px 10px', fontFamily: S.ui, fontSize: 13, border: `1px solid ${S.fieldLine}`, borderRadius: 6, background: S.field, '&:focus': { outline: 'none', borderColor: S.fg } },
  answer: { margin: 0, padding: '10px 12px', borderRadius: 8, background: S.field, border: `1px solid ${S.line}`, whiteSpace: 'pre-wrap', lineHeight: 1.5 }
});

/** How the live version of the page is doing, from the existing page analytics. */
const PulsePanel: React.FC<Props> = ({ pageId, path, publishedVersion }) => {
  const c = useStyles();
  const [range, setRange] = useState<Range>('7');
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [state, setState] = useState<'idle' | 'loading' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState<{ text: string; error?: boolean } | null>(null);
  const [asking, setAsking] = useState(false);
  const target = analyticsPath(path);

  const load = useCallback(async () => {
    if (!publishedVersion) return;
    setState('loading');
    try {
      const response = await fetch('/api/analytics-agent/pages/metrics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: target, ...dateRange(Number(range)), version: publishedVersion })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status !== 'success') throw new Error(data.error || 'The analytics service didn’t answer.');
      setMetrics(data.data);
      setState('idle');
    } catch (e) {
      setMetrics(null);
      setMessage((e as Error).message);
      setState('error');
    }
  }, [target, range, publishedVersion]);

  useEffect(() => { load(); }, [load]);

  const ask = async (e: React.FormEvent) => {
    e.preventDefault();
    const q = question.trim();
    if (!q) return;
    setAsking(true);
    try {
      const response = await fetch('/api/analytics-agent/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: `For page ${target}: ${q}`, conversation_history: [], version: publishedVersion, ...dateRange(Number(range)) })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.response) throw new Error(data.detail || data.error || 'No answer.');
      setAnswer({ text: data.response });
    } catch (err) {
      setAnswer({ text: (err as Error).message, error: true });
    } finally {
      setAsking(false);
    }
  };

  if (!publishedVersion) return <p className={c.muted}>This page isn’t live yet. Its numbers show here once a version is published.</p>;

  const scroll = (metrics && metrics.scroll && metrics.scroll.milestone_percentages) || {};
  const top = ((metrics && metrics.clicks && metrics.clicks.top_elements) || []).slice(0, 5);
  return (
    <div className={c.root} data-testid="pulse-panel">
      <div className={c.row}>
        <span className={c.version}>Live v{publishedVersion}</span>
        <div className={c.segment} role="group" aria-label="Period">
          {(['7', '30', '90'] as Range[]).map(r => (
            <button key={r} type="button" className={`${c.seg} ${range === r ? c.segOn : ''}`} aria-pressed={range === r} onClick={() => setRange(r)}>{r} days</button>
          ))}
        </div>
      </div>
      {state === 'loading' && !metrics && <p className={c.muted}>Loading…</p>}
      {state === 'error' && <p className={c.error} role="alert">Couldn’t load the numbers: {message}</p>}
      {metrics && (
        <>
          <div className={c.stats}>
            <div className={c.stat}><div className={c.statValue} data-stat="views">{number(metrics.views && metrics.views.total_views)}</div><div className={c.statLabel}>Views</div></div>
            <div className={c.stat}><div className={c.statValue} data-stat="visitors">{number(metrics.views && metrics.views.unique_visitors)}</div><div className={c.statLabel}>Visitors</div></div>
            <div className={c.stat}><div className={c.statValue} data-stat="time">{(metrics.duration && metrics.duration.average_duration_formatted) || '–'}</div><div className={c.statLabel}>Avg. time</div></div>
            <div className={c.stat}><div className={c.statValue} data-stat="clicks">{number(metrics.clicks && metrics.clicks.total_clicks)}</div><div className={c.statLabel}>Clicks</div></div>
          </div>
          <section>
            <div className={c.label}>How far visitors scroll</div>
            {MILESTONES.map(m => (
              <div key={m} className={c.bar} data-milestone={m}>
                <span>{m}%</span>
                <span className={c.track}><span className={c.fill} style={{ display: 'block', width: `${Math.max(0, Math.min(100, scroll[m] || 0))}%` }} /></span>
                <span>{Math.round(scroll[m] || 0)}%</span>
              </div>
            ))}
          </section>
          {!!top.length && (
            <section>
              <div className={c.label}>Most clicked</div>
              <ul className={c.clicks}>
                {top.map(t => (
                  <li key={`${t.element}-${t.text}`} className={c.click} title={t.href || t.element}>
                    <span className={c.clickText}>{(t.text || '').trim() || t.tagName || t.element}</span>
                    <span className={c.version}>{number(t.clicks)}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
      <form className={c.ask} onSubmit={ask}>
        <div className={c.label}>Ask about this page</div>
        <input className={c.input} value={question} onChange={e => setQuestion(e.target.value)} placeholder="Where do visitors stop reading?" aria-label="Ask the analytics assistant" disabled={asking} />
        {asking && <p className={c.muted}>Thinking…</p>}
        {answer && <p className={answer.error ? c.error : c.answer} data-testid="pulse-answer">{answer.text}</p>}
      </form>
      <a className={c.link} href={`/admin/page/${pageId}/analytics?version=${encodeURIComponent(publishedVersion)}`}>Open full analytics</a>
    </div>
  );
};

export default PulsePanel;
